import { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Select,
  SelectItem,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Autocomplete,
  AutocompleteItem,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import InformationTooltip from '../../../../../common/src/components/ui/InformationTooltip';
import { toast } from 'react-toastify';

import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { fetchUniqueValues } from 'common/src/api/others';
import { UniqueValueAnalysis } from 'common/src/utils/types';
import { TABLES_CONFIG } from 'common/src/utils/definitions';
import { supabase } from 'common/src/supabase';
import { LoadingState, PageHeader, PageLayout } from '../../../components/ui';

const SuperAdminSettings = () => {
  const [selectedTable, setSelectedTable] = useState('settlements');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [loading, setLoading] = useState(false);
  const [uniqueValues, setUniqueValues] = useState<UniqueValueAnalysis[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<{
    original: string;
    new: string | null;
  }>({ original: '', new: null });
  const [updateAllExporters, setUpdateAllExporters] = useState(true);
  const [selectedExporter, setSelectedExporter] = useState<string>('');
  const [exporters, setExporters] = useState<{ id: string; name: string }[]>(
    []
  );
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);

  // Function to visualize trailing and leading spaces
  const visualizeValue = (value: string) => {
    const hasTrailingSpace = value.endsWith(' ');
    const hasLeadingSpace = value.startsWith(' ');
    const trailingSpaces = hasTrailingSpace
      ? value.length - value.trimEnd().length
      : 0;
    const leadingSpaces = hasLeadingSpace
      ? value.length - value.trimStart().length
      : 0;

    // Only visualize trailing and leading spaces, keep middle spaces as is
    let visualizedValue = value;
    if (hasTrailingSpace) {
      visualizedValue = value.trimEnd() + 'X'.repeat(trailingSpaces);
    }
    if (hasLeadingSpace) {
      visualizedValue = 'X'.repeat(leadingSpaces) + value.trimStart();
    }

    return (
      <div className='flex items-center gap-2'>
        <span
          className={`font-mono ${hasTrailingSpace || hasLeadingSpace
              ? 'bg-yellow-50 px-1 rounded'
              : ''
            }`}
        >
          {visualizedValue}
        </span>
        {(hasTrailingSpace || hasLeadingSpace) && (
          <Chip size='sm' variant='flat' color='warning' className='h-5'>
            {`${hasLeadingSpace ? `${leadingSpaces} al inicio` : ''}${hasLeadingSpace && hasTrailingSpace ? ' y ' : ''
              }${hasTrailingSpace ? `${trailingSpaces} al final` : ''}`}
          </Chip>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (selectedTable && selectedColumn) {
      loadUniqueValues();
    }
  }, [selectedTable, selectedColumn]);

  const loadUniqueValues = async () => {
    try {
      setLoading(true);
      const values = await fetchUniqueValues(selectedTable, selectedColumn);
      setUniqueValues(values);
    } catch (error) {
      console.error('Error loading unique values:', error);
      // Add proper error handling/notification here
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Valor',
      'Frecuencia',
      'Última Aparición',
      'Valores Similares',
    ];
    const csvContent = [
      headers.join(','),
      ...uniqueValues.map((item) =>
        [
          item.value,
          item.count,
          item.lastSeen,
          item.similarValues?.join(';') || '',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedTable}_${selectedColumn}_analysis.csv`;
    link.click();
  };

  const handleValueUpdate = async () => {
    if (!selectedValue.new) {
      toast.error('Debe seleccionar un nuevo valor');
      return;
    }

    try {
      setLoading(true);

      let query;

      if (selectedTable === 'settlements_details') {
        if (!updateAllExporters) {
          // Primero obtenemos los IDs de settlements
          const { data: settlementIds, error: settlementError } = await supabase
            .from('settlements')
            .select('id')
            .eq('exporter_id', selectedExporter);

          if (settlementError) throw settlementError;

          // Luego actualizamos los detalles que corresponden a esos settlements
          query = supabase
            .from(selectedTable)
            .update({ [selectedColumn]: selectedValue.new })
            .eq(selectedColumn, selectedValue.original)
            .in(
              'settlement_id',
              settlementIds.map((s) => s.id)
            );
        } else {
          query = supabase
            .from(selectedTable)
            .update({ [selectedColumn]: selectedValue.new })
            .eq(selectedColumn, selectedValue.original);
        }
      } else {
        // Para otras tablas, usamos la lógica original
        query = supabase
          .from(selectedTable)
          .update({ [selectedColumn]: selectedValue.new })
          .eq(selectedColumn, selectedValue.original);

        if (TABLES_CONFIG[selectedTable].hasExporter && !updateAllExporters) {
          query = query.eq('exporter_id', selectedExporter);
        }
      }

      const { error } = await query;
      if (error) throw error;

      await loadUniqueValues();
      toast.success('Valores actualizados correctamente');
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating values:', error);
      toast.error('Error al actualizar los valores');
    } finally {
      setLoading(false);
    }
  };

  const scrollToValue = (value: string) => {
    setHighlightedValue(value);
    const element = document.getElementById(`row-${value}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Remove highlight after 2 seconds
      setTimeout(() => setHighlightedValue(null), 2000);
    }
  };

  const renderCell = (item: UniqueValueAnalysis) => {
    return (
      <TableRow
        id={`row-${item.value}`}
        className={
          highlightedValue === item.value
            ? 'bg-yellow-100 transition-colors duration-500'
            : ''
        }
      >
        <TableCell>
          <button
            onClick={() => {
              setSelectedValue({ original: item.value, new: null });
              setIsEditModalOpen(true);
            }}
            className='text-left hover:text-blue-500 hover:underline w-full'
          >
            {visualizeValue(item.value || '-')}
          </button>
        </TableCell>
        <TableCell>{item.count.toLocaleString()}</TableCell>
        <TableCell>
          {format(new Date(item.lastSeen), 'dd/MM/yyyy HH:mm', { locale: es })}
        </TableCell>
        <TableCell>
          {item.similarValues && item.similarValues.length > 0 ? (
            <div className='flex flex-wrap gap-1'>
              {item.similarValues.map((similar, idx) => {
                const hasTrailingSpace = similar.endsWith(' ');
                const hasLeadingSpace = similar.startsWith(' ');
                let visualizedSimilar = similar;

                if (hasTrailingSpace) {
                  const trailingSpaces =
                    similar.length - similar.trimEnd().length;
                  visualizedSimilar =
                    similar.trimEnd() + 'X'.repeat(trailingSpaces);
                }
                if (hasLeadingSpace) {
                  const leadingSpaces =
                    similar.length - similar.trimStart().length;
                  visualizedSimilar =
                    'X'.repeat(leadingSpaces) + similar.trimStart();
                }

                return (
                  <Chip
                    key={idx}
                    size='sm'
                    variant='flat'
                    color='warning'
                    className={`cursor-pointer hover:opacity-80 ${hasTrailingSpace || hasLeadingSpace ? 'font-mono' : ''
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollToValue(similar);
                    }}
                  >
                    {visualizedSimilar}
                  </Chip>
                );
              })}
            </div>
          ) : (
            '-'
          )}
        </TableCell>
      </TableRow>
    );
  };

  const loadExporters = async () => {
    const { data, error } = await supabase
      .from('exporters')
      .select('id, name')
      .order('name');

    if (error) {
      console.error('Error loading exporters:', error);
      return;
    }

    setExporters(data || []);
  };

  useEffect(() => {
    if (isEditModalOpen && TABLES_CONFIG[selectedTable].hasExporter) {
      loadExporters();
    }
  }, [isEditModalOpen, selectedTable]);

  return (
    <PageLayout>
      <PageHeader
        title='Configuración del Sistema'
        titleAddon={
          <InformationTooltip>
            <p className='text-sm font-semibold mb-2'>Análisis de Datos</p>
            <ul className='list-disc ml-4 mt-1 text-sm space-y-2'>
              <li>
                <span className='font-medium'>Objetivo:</span> Detectar
                inconsistencias en los datos como valores duplicados o mal
                escritos
              </li>
              <li>
                <span className='font-medium'>Uso:</span> Selecciona una tabla y
                columna para ver todos sus valores únicos y su frecuencia de uso
              </li>
            </ul>
          </InformationTooltip>
        }
      />

      <div className='flex gap-4 mb-6'>
        <Select
          label='Tabla'
          className='w-64'
          value={selectedTable}
          onChange={(e) => {
            setSelectedTable(e.target.value);
            setSelectedColumn('');
          }}
        >
          {Object.entries(TABLES_CONFIG).map(([key, value]) => (
            <SelectItem key={key} value={key}>
              {value.name}
            </SelectItem>
          ))}
        </Select>

        <Select
          label='Columna'
          className='w-64'
          value={selectedColumn}
          onChange={(e) => setSelectedColumn(e.target.value)}
          isDisabled={!selectedTable}
        >
          {(selectedTable ? TABLES_CONFIG[selectedTable].columns : []).map(
            (column) => (
              <SelectItem key={column.key} value={column.key}>
                {column.name}
              </SelectItem>
            )
          )}
        </Select>
      </div>

      {selectedColumn && (
        <Card>
          <CardHeader className='flex justify-between'>
            <h2 className='text-lg font-semibold'>
              Valores únicos: {selectedColumn}
            </h2>
            <Button
              size='sm'
              color='primary'
              onClick={exportToCSV}
              startContent={<Icon icon='tabler:download' />}
            >
              Exportar
            </Button>
          </CardHeader>
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              <Table removeWrapper aria-label='Valores únicos'>
                <TableHeader>
                  <TableColumn>VALOR</TableColumn>
                  <TableColumn>FRECUENCIA</TableColumn>
                  <TableColumn>ÚLTIMA APARICIÓN</TableColumn>
                  <TableColumn>VALORES SIMILARES</TableColumn>
                </TableHeader>
                <TableBody>
                  {uniqueValues.map((item, index) => renderCell(item))}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>
      )}

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            Editar Valor
          </ModalHeader>
          <ModalBody>
            <div className='flex flex-col gap-4'>
              <div className='p-4 bg-default-50 rounded-lg'>
                <p className='text-sm text-default-600 mb-1'>Valor Actual:</p>
                {visualizeValue(selectedValue.original)}
              </div>

              <div className='space-y-2'>
                <p className='text-sm text-default-600'>
                  Seleccione el nuevo valor que reemplazará al valor actual:
                </p>
                <Autocomplete
                  label='Nuevo Valor'
                  selectedKey={selectedValue.new || undefined}
                  onSelectionChange={(key) =>
                    setSelectedValue({ ...selectedValue, new: key as string })
                  }
                  className='w-full'
                >
                  {uniqueValues.map((item) => (
                    <AutocompleteItem
                      key={item.value}
                      value={item.value}
                      textValue={item.value}
                    >
                      <div className='flex justify-between items-center w-full'>
                        {visualizeValue(item.value || '-')}
                        <span className='text-sm text-default-500 ml-2'>
                          ({item.count.toLocaleString()})
                        </span>
                      </div>
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>

              {(TABLES_CONFIG[selectedTable].hasExporter ||
                selectedTable === 'settlements_details') && (
                  <div className='space-y-4 border-t pt-4'>
                    <p className='text-sm text-default-600 font-medium'>
                      Alcance del cambio:
                    </p>

                    <label className='flex items-center gap-2 cursor-pointer hover:bg-default-50 p-2 rounded-lg transition-colors'>
                      <input
                        type='checkbox'
                        checked={updateAllExporters}
                        onChange={(e) => setUpdateAllExporters(e.target.checked)}
                        className='form-checkbox h-4 w-4 text-brand-600'
                      />
                      <div>
                        <p className='font-medium'>
                          Actualizar para todas las exportadoras
                        </p>
                        <p className='text-sm text-default-500'>
                          El cambio se aplicará en todas las exportadoras que
                          contengan este valor
                        </p>
                      </div>
                    </label>

                    {!updateAllExporters && (
                      <div className='pl-6'>
                        <p className='text-sm text-default-600 mb-2'>
                          Seleccione la exportadora específica donde se aplicará
                          el cambio:
                        </p>
                        <Select
                          label='Exportadora'
                          value={selectedExporter}
                          onChange={(e) => setSelectedExporter(e.target.value)}
                          isRequired
                        >
                          {exporters.map((exporter) => (
                            <SelectItem key={exporter.id} value={exporter.id}>
                              {exporter.name}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setIsEditModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={handleValueUpdate}
              isDisabled={
                !selectedValue.new || (!updateAllExporters && !selectedExporter)
              }
              isLoading={loading}
            >
              Guardar Cambios
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperAdminSettings;
